import React, { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { useParams, useLocation } from "react-router-dom";
import { Spinner } from "reactstrap";
import { getPropertiesByUser } from "services/userServices";
import "../../assets/scss/style.css";
import {Row,Card,Col,Button } from "reactstrap";
import { useHistory } from "react-router-dom";

const UserPropertyDetailPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { userId, propertyId } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get context from the navigation state
  const fromPage = location.state?.fromPage || 1;

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        console.log("Looking for user property ID:", propertyId);
        console.log("User ID:", userId);
        console.log("From page:", fromPage);
        
        let found = null;
        
        // Strategy 1: Search the specific page we came from first
        console.log(`Searching the page we came from (page ${fromPage})...`);
        const contextRes = await getPropertiesByUser(userId, fromPage, 10, "", "", "");
        const contextItems = contextRes?.data?.items || [];
        found = contextItems.find((item) => item._id === propertyId);
        
        if (found) {
          console.log(`Property found on the original page ${fromPage}`);
          setProperty(found);
          return;
        }
        
        // Strategy 2: Search through multiple pages systematically
        console.log("Original page search failed, trying systematic pagination search...");
        let currentPage = 1;
        const maxPagesToSearch = 5;
        const pageSize = 10;
        
        while (currentPage <= maxPagesToSearch && !found) {
          // Skip the page we already searched
          if (currentPage === fromPage) {
            currentPage++;
            continue;
          }
          
          console.log(`Searching page ${currentPage}...`);
          const res = await getPropertiesByUser(userId, currentPage, pageSize, "", "", "");
          const items = res?.data?.items || [];
          
          found = items.find((item) => item._id === propertyId);
          
          if (found) {
            console.log(`Property found on page ${currentPage}`);
            setProperty(found);
            return;
          }
          
          currentPage++;
        }
        
        console.error("Property not found after searching multiple pages");
        setProperty(null);
        
      } catch (err) {
        console.error("Failed to fetch property", err);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId, userId, fromPage]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center items-center p-10">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-10 text-center text-red-500 font-semibold">
        <h3>❌ Property not found</h3>
        <p style={{ marginTop: 10, fontSize: 16, color: "#666" }}>
          Property with ID "{propertyId}" could not be found.
        </p>
        <Button
          className='btn all-common-btn'
          style={{ marginTop: 20 }}
          onClick={() => history.push(`/admin/users/${userId}/properties`)}
        >
          ← Back to User Properties
        </Button>
      </div>
    );
  }

  return (
    // Use paddingTop to avoid overlapping header (adjust the px as needed)
    <div style={{ minHeight: "100vh", background: "#fafaf8", padding: "90px 0 32px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <Row className="mb-3">
          <Col className="d-flex justify-content-end">
            <Button
              className='btn all-common-btn'
              outline
              onClick={() => history.push(`/admin/users/${userId}/properties`)}
            >
              ← Back to User Properties
            </Button>
          </Col>
        </Row>

        <h2
          style={{
            fontWeight: 700,
            fontSize: "2rem",
            marginBottom: "28px",
            color: "#214",
            textAlign: "left"
          }}
        >
          User Property Details
        </h2>
        {/* Main property details section */}
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Main Image */}
          <div style={{ flex: "0 0 560px" }}>
            <div
              style={{
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: "0 4px 24px rgba(48,60,71,.12)",
                background: "#fff"
              }}
            >
              <img
                src={property?.photos?.[0] || "https://via.placeholder.com/600"}
                alt={property.title || "Property"}
                style={{
                  width: "100%",
                  height: 350,
                  objectFit: "cover",
                  display: "block"
                }}
              />
            </div>
          </div>

          {/* Listing Price and Main Info */}
          <div style={{ flex: 1, minWidth: 270 }}>
            <h1 style={{ fontSize: "2.5rem", fontWeight: 700, margin: 0, marginBottom: 10 }}>
              {property.listing_price
                ? `$${Number(property.listing_price).toLocaleString()}`
                : "Price on request"}
            </h1>
            {/* Commission badge */}
            <div
              style={{
                background: "#19af5b",
                color: "#fff",
                borderRadius: 32,
                padding: "10px 20px",
                fontWeight: 600,
                display: "inline-block",
                marginBottom: 18,
                fontSize: 19,
                marginTop: 0
              }}
            >
              {property.buyer_agent_commission
                ? `${property.buyer_agent_commission}% Buyer Agent Commission`
                : "2% Buyer Agent Commission"}
              <span style={{ marginLeft: 8, fontSize: 19, verticalAlign: "middle" }}>ℹ️</span>
            </div>
            {/* Address */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                color: "#2d816b",
                margin: "20px 0",
                fontWeight: 500,
                fontSize: 18
              }}
            >
              <MapPin style={{ marginRight: 8, color: "#19af5b" }} />
              {property.address || "Address not available"}
            </div>
            {/* Map */}
            <div style={{ margin: "18px 0 0 0", borderRadius: 13, overflow: "hidden", width: "fit-content" }}>
              <iframe
                title="map"
                width={240}
                height={120}
                style={{ border: 0, borderRadius: 13 }}
                loading="lazy"
                allowFullScreen
                src={`https://maps.google.com/maps?q=${encodeURIComponent(property.address || "")}&output=embed`}
              />
            </div>
          </div>
        </div>

        {/* Facts and Features */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "34px auto 0 auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 17, letterSpacing: ".2px" }}>
            Facts & Features
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.listingType || "Property"}
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.property_square_footage || 0} /sqft
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.year_built || "Year not listed"}
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.lot_size || "0-acre lot"}
              </div>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Property Details
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.total_rooms || 0} Total Rooms
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.total_bathrooms || 0} Bathrooms
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#19af5b", fontWeight: 700, fontSize: 17 }}>
                {property.total_bedrooms || 0} Bedrooms
              </div>
            </div>
          </div>
        </div>

        {/* Amenities */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Amenities
          </div>
          <div style={{ color: "#566", fontSize: 18, minHeight: 30 }}>
            {(() => {
              // Combine all amenity fields
              const allAmenities = [
                ...(property.amenities || []),
                ...(property.additional_amenities || []),
                ...(property.building_amenities || []),
                ...(property.outdoor_amenities || []),
                ...(property.other_amenities || [])
              ].filter(Boolean);
              
              console.log("All amenities:", allAmenities);
              console.log("Property amenities data:", {
                amenities: property.amenities,
                additional_amenities: property.additional_amenities,
                building_amenities: property.building_amenities,
                outdoor_amenities: property.outdoor_amenities,
                other_amenities: property.other_amenities
              });
              
              if (allAmenities.length > 0) {
                return (
                  <ul style={{ paddingLeft: 16, color: "#355" }}>
                    {allAmenities.map((amenity, i) => (
                      <li key={i} style={{ marginBottom: 8 }}>{amenity}</li>
                    ))}
                  </ul>
                );
              } else {
                return (
                  <span style={{ color: "#859", fontStyle: "italic" }}>
                    This seller has not listed any amenities for this listing. Please contact the seller to know more.
                  </span>
                );
              }
            })()}
          </div>
        </div>

        {/* Description */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Description
          </div>
          <div style={{ color: "#566", fontSize: 18, minHeight: 40 }}>
            {property.description || (
              <span style={{ color: "#859", fontStyle: "italic" }}>
                No description available.
              </span>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div
          style={{
            background: "#fff",
            borderRadius: 15,
            margin: "30px auto",
            padding: 24,
            boxShadow: "0 2px 18px rgba(60,60,110,0.07)"
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 23, color: "#214", marginBottom: 13 }}>
            Contact Information
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {property.phone_number && (
              <div
                style={{
                  background: "#f5faf6",
                  padding: 16,
                  borderRadius: 10,
                  minWidth: 250,
                  flex: "1 1 240px",
                  marginBottom: 10
                }}
              >
                <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Phone Number</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                  {property.phone_number}
                </div>
              </div>
            )}
            {property.email && (
              <div
                style={{
                  background: "#f5faf6",
                  padding: 16,
                  borderRadius: 10,
                  minWidth: 280,
                  flex: "1 1 260px",
                  marginBottom: 10
                }}
              >
                <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Email</div>
                <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                  {property.email}
                </div>
              </div>
            )}
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Status</div>
              <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                {property.status ? property.status.charAt(0).toUpperCase() + property.status.slice(1) : "Pending"}
              </div>
            </div>
            <div
              style={{
                background: "#f5faf6",
                padding: 16,
                borderRadius: 10,
                minWidth: 180,
                flex: "1 1 160px",
                marginBottom: 10
              }}
            >
              <div style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>Approved</div>
              <div style={{ color: "#19af5b", fontWeight: 600, fontSize: 16 }}>
                {property.is_approved ? "Yes" : "No"}
              </div>
            </div>
          </div>
        </div>

        {/* Footer-style stats */}
        <div
          style={{
            display: "flex",
            gap: 40,
            margin: "40px 0 8px 0",
            fontSize: 17,
            color: "#666",
            justifyContent: "start"
          }}
        >
          <div>
            <span style={{ fontWeight: 600 }}>0 days</span> on Quotivo
          </div>
          <div>
            <span style={{ fontWeight: 600 }}>{property.views || 0}</span> Views
          </div>
          <div>
            <span style={{ fontWeight: 600 }}>{property.saves || 0}</span> Saves
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPropertyDetailPage;
